import React, { useEffect, useMemo, useState } from 'react';
import { Text, Platform, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { getDailyLogData, updateEmployeeStatus } from '../../../api/Timeclock';
import { BasicLayout, CommonContainer } from '../../../common/components/CustomLayout';
import { BOHTBody, BOHTD, BOHTDPressable, BOHTH2, BOHTHead, BOHTR, BOHTable } from '../../../common/components/bohtable';
import { msgStr } from '../../../common/constants/Message';
import { useAlertModal } from '../../../common/hooks';
import { BOHToolbar, renderBOHTlbDatePicker } from '../../../common/components/bohtoolbar';
import { TextdefaultSize } from '../../../common/constants/Fonts';
import { formatDate } from '../../../common/utils/DateUtils';

// Helper function to parse time in various formats and convert to seconds
const parseTimeToSeconds = (timeStr: string) => {
  if (!timeStr || typeof timeStr !== 'string') return 0;
  
  // Handle empty strings
  timeStr = timeStr.trim();
  if (timeStr === '') return 0;
  
  // Try to handle HH:MM:SS format
  if (timeStr.includes(' ')) {
    const parts = timeStr.split(' ');
    if (parts.length === 3) {
      // HH:MM:SS format
      const hours = parseInt(parts[0], 10) || 0;
      const minutes = parseInt(parts[1], 10) || 0;
      const seconds = parseInt(parts[2], 10) || 0;
      return hours * 3600 + minutes * 60 + seconds;
    } else if (parts.length === 2) {
      // MM:SS format
      const minutes = parseInt(parts[0], 10) || 0;
      const seconds = parseInt(parts[1], 10) || 0;
      return minutes * 60 + seconds;
    }
  }
  
  // Handle formats like "4h 30m 15s" or "4m 55s" or "30s"
  let totalSeconds = 0;
  
  // Extract hours
  const hoursMatch = timeStr.match(/(\d+)h/);
  if (hoursMatch) {
    totalSeconds += parseInt(hoursMatch[1], 10) * 3600;
  }
  
  // Extract minutes
  const minutesMatch = timeStr.match(/(\d+)m/);
  if (minutesMatch) {
    totalSeconds += parseInt(minutesMatch[1], 10) * 60;
  }
  
  // Extract seconds
  const secondsMatch = timeStr.match(/(\d+)s/);
  if (secondsMatch) {
    totalSeconds += parseInt(secondsMatch[1], 10);
  }
  
  return totalSeconds;
};

// Helper function to format seconds to time display
const formatSecondsToTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  
  return `${hours}h ${minutes}m ${seconds}s`;
};

const DailyLog = ({ navigation }) => {
  const { showAlert } = useAlertModal();
  
  const [tableData, setTableData] = useState([]);
  const [updateLocationTrigger, setUpdateLocationsTrigger] = useState(false);
  const InitialWidths = [160, 120];
  const [searchKey, setSearchKey] = useState('');
  const [daysArray, setdaysArray] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [sorting, setSorting] = useState({
    key: null,
    direction: null,
  })
  
  const reloadInterval = 5;
  const [countdown, setCountdown] = useState(reloadInterval);

  const today = new Date();
  const sevenDaysBeforeToday = new Date(today);
  sevenDaysBeforeToday.setDate(today.getDate() - 7);

  const [searchOptions, setSearchOptions] = useState({
    start_date: formatDate(sevenDaysBeforeToday),
    end_date: formatDate(today),
  });

  const changeSearchOptions = (key, val) => {
    setSearchOptions(prevOptions => ({
      ...prevOptions,
      [key]: val
    }));
  }

  useEffect(() => {
    setLoading(true);
    setUpdateLocationsTrigger(true);
  }, [searchOptions])

  useEffect(() => {
    if (updateLocationTrigger == true) getTable();
  }, [updateLocationTrigger]);

  useEffect(() => {
    setUpdateLocationsTrigger(true);
  }, [searchKey])

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          getTable();
          return reloadInterval;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [searchOptions]);

  const getTable = () => {
    setLoading(true);
    getDailyLogData({ searchOptions }, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setUpdateLocationsTrigger(false);
          setTableData(jsonRes.gridData);
          // setTotalNights(jsonRes.totalNights);
          setdaysArray(jsonRes.daysArray);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
      setLoading(false);
    });
  };
  
  const openDetail = (searchOptions) => {
    console.log(searchOptions);
    AsyncStorage.setItem('__logdetail_options', JSON.stringify(searchOptions))

    if (Platform.OS == 'web') {
      window.open('/home/logdetail');
    }
  }

  const handleSort = (key:string) => {
    const sortsUpdate = {
      key: key,
      direction: sorting.key == key && sorting.direction == 'ascending' ? 'descending' : 'ascending'
    }
    setSorting(sortsUpdate)
  }

  const getStatusFormat = (status) => {
    let color;
    let statusText;
  
    switch (status) {
      case 0:
        color = 'red';
        statusText = "Clocked Out";
        break;
      case 1:
        color = 'green';
        statusText = "Clocked In";
        break;
      case 2:
        color = 'orange';
        break;
      case 3:
        color = 'blue';
        statusText = "Lunch";
        break;
      default:
        color = 'gray';
        statusText = "Unknown Status";
    }
  
    return (
      <View style={{flexDirection: 'row', alignItems: 'center',}}>
        <View style={[{ width: 10, height: 10, borderRadius: 5, marginRight: 8, backgroundColor: color }]} />
        <Text>{statusText}</Text>
      </View>
    );
  };
  
  const updateStatus = (status: number, user_id: string): void => {
    if (status !== 1 && status !== 0) {
      return;
    }

    setLoading(true);
    const payload = {
        status: status ? 0 : 1,
        user_id,
    }
    updateEmployeeStatus(payload, (jsonRes, status, error) => {
      setLoading(false);
      switch (status) {
          case 200:
            setUpdateLocationsTrigger(true);
            setCountdown(reloadInterval);
            break;
          case 500:
            showAlert('error', msgStr('serverError'));
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else if (error) showAlert('error', error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
    });
  }

  const renderTableData = () => {
            
    if(sorting.key) 
    tableData.sort((a, b) => {
      if (a[sorting.key] < b[sorting.key]) return sorting.direction === 'ascending' ? -1 : 1;
      if (a[sorting.key] > b[sorting.key]) return sorting.direction === 'ascending' ? 1 : -1;
      return 0;
    });

    const rows = [];
    if (tableData && tableData.length && tableData.length > 0) {
      tableData.map((item, index) => {
        rows.push(
          <BOHTR key={index}>
            <BOHTD width={InitialWidths[0]}>{item.name}</BOHTD>
            <BOHTDPressable width={InitialWidths[1]} onPress={()=>updateStatus(item.currentStatus, item.user_id)}>{getStatusFormat(item.currentStatus)}</BOHTDPressable>
            {daysArray.length > 0 && daysArray.map((day, index) => (
              <BOHTDPressable 
                key={index} 
                width={InitialWidths[1]} 
                textAlign='right'
                onPress={()=>{
                  openDetail({
                    user_id: item.user_id,
                    date: day.date
                  })
                }}>{item.logs[day.formattedDate] || ""}</BOHTDPressable>
            ))}
            <BOHTD width={InitialWidths[1]}>
              {daysArray.length > 0 ? 
                formatSecondsToTime(daysArray.reduce((total, day) => {
                  const dayHours = item.logs[day.formattedDate] || "";
                  return total + parseTimeToSeconds(dayHours);
                }, 0))
              : "0h 0m 0s"}
            </BOHTD>
          </BOHTR>
        );
      });
    } else {
      <></>;
    }
    return <>{rows}</>;
  };
  

  const headers = [
    { key: 'Driver', label: 'Driver' },
    { key: 'CurrentStatus', label: 'Current Status' },
    // { key: 'Total', label: 'Total Time' },
  ];
  const tableElement = useMemo(() => (
    <BOHTable>
      <BOHTHead>
        <BOHTR>
          {headers.map(({ key, label }, wIndex) => (
            <BOHTH2
              key={key}
              width={InitialWidths[wIndex]}
              isSorted={sorting.key === key}
              direction={sorting.key === key ? sorting.direction : null}
              onClick={() => handleSort(key)}
            >
              {label}
            </BOHTH2>
          ))}
          {daysArray.length > 0 && daysArray.map((item, index) => (
            <BOHTH2
              key={index}
              width={InitialWidths[1]}
              style={{
                textAlign: 'center'
              }}
            >{item.formattedDate}</BOHTH2>
          ))}
           <BOHTH2
              key={'Total'}
              width={InitialWidths[1]}
              isSorted={sorting.key === 'Total'}
              direction={sorting.key === 'Total' ? sorting.direction : null}
              onClick={() => handleSort('Total')}
            >
              {"Total Hours"}
            </BOHTH2>
        </BOHTR>
      </BOHTHead>
      <BOHTBody>
        {renderTableData()}
      </BOHTBody>
    </BOHTable>
  ), [tableData, sorting]);

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        navigation.navigate('Marketing');
      }}
      screenName={'TimeClock Logs'}
      // isLoading={isLoading}
    >
      <CommonContainer>
        <BOHToolbar style={{ zIndex: 10 }}>
          <Text style={{ marginRight: 8, fontSize: TextdefaultSize }}>Start</Text>
          {Platform.OS == 'web' &&
            renderBOHTlbDatePicker(searchOptions.start_date, (date) => {
              const year = date.getFullYear();
              const formattedDate = `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
              changeSearchOptions('start_date', formattedDate);
            })}
          <Text style={{ marginHorizontal: 8, fontSize: TextdefaultSize }}>End</Text>
          {Platform.OS == 'web' &&
            renderBOHTlbDatePicker(searchOptions.end_date, (date) => {
              const year = date.getFullYear();
              const formattedDate = `${year}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
              changeSearchOptions('end_date', formattedDate);
            })}
          {/* <BOHButton
            style={{ marginLeft: 30 }}
            label='Export'
            onPress={exportForecasting}
          /> */}
          <Text style={{ marginLeft: 30, fontSize: TextdefaultSize, color:'#D9534F' }}>Reload in {countdown} seconds</Text>
        </BOHToolbar>
        {tableElement}
      </CommonContainer>
    </BasicLayout>
  );
};

export default DailyLog;
